import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Play, Pause, Maximize2, Minimize2,
  CheckCircle, Clock, Monitor, AlertCircle, Save, XCircle, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SCORM 1.2 API Adapter
 * Implements the SCORM 1.2 Run-Time Environment API.
 * The SCO (loaded in an iframe) finds this via window.API
 */
class ScormAPI {
  constructor(onCommit, onFinish) {
    this._data = {};
    this._initialized = false;
    this._finished = false;
    this._lastError = "0";
    this._onCommit = onCommit;
    this._onFinish = onFinish;
    this._dirty = false;
  }

  loadData(cmiData) {
    this._data = this._flatten(cmiData);
  }

  _flatten(obj, prefix = '') {
    const result = {};
    for (const key in obj) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        Object.assign(result, this._flatten(obj[key], path));
      } else {
        result[path] = String(obj[key] ?? '');
      }
    }
    return result;
  }

  _unflatten() {
    const result = {};
    for (const key in this._data) {
      const parts = key.split('.');
      let current = result;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = this._data[key];
    }
    return result;
  }

  LMSInitialize(param) {
    if (this._initialized) { this._lastError = "101"; return "false"; }
    this._initialized = true;
    this._finished = false;
    this._lastError = "0";
    return "true";
  }

  LMSFinish(param) {
    if (!this._initialized) { this._lastError = "301"; return "false"; }
    if (this._dirty) this.LMSCommit("");
    this._initialized = false;
    this._finished = true;
    this._lastError = "0";
    if (this._onFinish) this._onFinish(this._unflatten());
    return "true";
  }

  LMSGetValue(element) {
    if (!this._initialized) { this._lastError = "301"; return ""; }
    this._lastError = "0";
    if (element in this._data) return this._data[element];
    // Handle special counts
    if (element.endsWith("._count")) return "0";
    this._lastError = "401";
    return "";
  }

  LMSSetValue(element, value) {
    if (!this._initialized) { this._lastError = "301"; return "false"; }
    this._lastError = "0";
    this._data[element] = String(value);
    this._dirty = true;
    return "true";
  }

  LMSCommit(param) {
    if (!this._initialized) { this._lastError = "301"; return "false"; }
    this._lastError = "0";
    this._dirty = false;
    if (this._onCommit) this._onCommit(this._unflatten());
    return "true";
  }

  LMSGetLastError() { return this._lastError; }

  LMSGetErrorString(errorCode) {
    const errors = {
      "0": "No Error", "101": "General Exception", "201": "Invalid Argument Error",
      "202": "Element Cannot Have Children", "203": "Element Not an Array",
      "301": "Not Initialized", "401": "Not Implemented Error",
      "402": "Invalid Set Value", "403": "Element Is Read Only", "404": "Element Is Write Only"
    };
    return errors[errorCode] || "Unknown Error";
  }

  LMSGetDiagnostic(param) { return this.LMSGetErrorString(this._lastError); }
}


const ScormPlayer = () => {
  const { scormId } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const apiRef = useRef(null);
  const containerRef = useRef(null);

  const [module, setModule] = useState(null);
  const [runtimeData, setRuntimeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lessonStatus, setLessonStatus] = useState('not attempted');
  const [scoreRaw, setScoreRaw] = useState(null);
  const [sessionTime, setSessionTime] = useState(0);
  const [started, setStarted] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Timer
  useEffect(() => {
    let interval;
    if (started) {
      interval = setInterval(() => setSessionTime(t => t + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [started]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const saveRuntime = useCallback(async (cmiData) => {
    try {
      await fetch(`${API}/scorm/${scormId}/runtime`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ cmi: cmiData })
      });
      setLastSaved(new Date());

      // Update local UI state
      const status = cmiData?.core?.lesson_status || cmiData?.cmi?.core?.lesson_status;
      if (status) setLessonStatus(status);
      const raw = cmiData?.core?.score?.raw || cmiData?.cmi?.core?.score?.raw;
      if (raw) setScoreRaw(raw);
    } catch (e) {
      console.error('Failed to save SCORM runtime:', e);
    }
  }, [scormId]);

  const handleFinish = useCallback(async (cmiData) => {
    await saveRuntime(cmiData);
    const status = cmiData?.core?.lesson_status;
    if (status === 'completed' || status === 'passed') {
      toast.success('Module SCORM terminé avec succès !');
    }
  }, [saveRuntime]);

  // Load module and runtime data
  useEffect(() => {
    if (!scormId) return;
    const load = async () => {
      try {
        const [modRes, rtRes] = await Promise.all([
          fetch(`${API}/scorm/${scormId}`, { credentials: 'include' }),
          fetch(`${API}/scorm/${scormId}/runtime`, { credentials: 'include' })
        ]);
        if (modRes.ok) setModule(await modRes.json());
        if (rtRes.ok) {
          const rt = await rtRes.json();
          setRuntimeData(rt);
          const status = rt?.cmi?.core?.lesson_status || 'not attempted';
          setLessonStatus(status);
          const raw = rt?.cmi?.core?.score?.raw;
          if (raw) setScoreRaw(raw);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [scormId]);

  // Initialize SCORM API and expose on window
  useEffect(() => {
    const api = new ScormAPI(saveRuntime, handleFinish);
    if (runtimeData?.cmi) api.loadData(runtimeData.cmi);
    apiRef.current = api;
    // SCORM 1.2: SCO looks for window.API in parent frames
    window.API = api;
    return () => { delete window.API; };
  }, [runtimeData, saveRuntime, handleFinish]);

  const handleStart = () => {
    setStarted(true);
    if (apiRef.current) {
      apiRef.current.LMSInitialize("");
      apiRef.current.LMSSetValue("cmi.core.lesson_status", "incomplete");
      apiRef.current.LMSSetValue("cmi.core.entry", "");
      apiRef.current.LMSCommit("");
    }
    setLessonStatus('incomplete');
  };

  const handleResume = () => {
    setStarted(true);
    if (apiRef.current) apiRef.current.LMSInitialize("");
  };

  const handleManualSave = async () => {
    if (apiRef.current) {
      apiRef.current.LMSCommit("");
      toast.success('Progression sauvegardée');
    }
  };

  const handleExit = async () => {
    if (apiRef.current && apiRef.current._initialized) {
      apiRef.current.LMSSetValue("cmi.core.session_time", formatScormTime(sessionTime));
      apiRef.current.LMSFinish("");
    }
    navigate(-1);
  };

  const formatScormTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(4, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Demo content simulation (when no real SCORM package is available)
  const DemoScormContent = () => {
    const [demoSlide, setDemoSlide] = useState(0);
    const slides = [
      { title: "Introduction", content: "Bienvenue dans ce module de formation. Ce contenu simule un package SCORM qui serait chargé dans cette zone." },
      { title: "Objectifs", content: "A l'issue de ce module, vous serez capable de comprendre les principes fondamentaux du sujet traité." },
      { title: "Contenu principal", content: "Voici le contenu principal de la formation. Dans un vrai package SCORM, ce serait du contenu HTML interactif." },
      { title: "Exercice pratique", content: "Mettez en pratique les concepts appris. Répondez aux questions et validez votre compréhension." },
      { title: "Conclusion", content: "Félicitations ! Vous avez terminé ce module. Votre progression a été enregistrée." },
    ];

    const goToSlide = (idx) => {
      setDemoSlide(idx);
      if (apiRef.current) {
        const pct = Math.round(((idx + 1) / slides.length) * 100);
        apiRef.current.LMSSetValue("cmi.core.lesson_location", String(idx));
        if (idx === slides.length - 1) {
          apiRef.current.LMSSetValue("cmi.core.lesson_status", "completed");
          apiRef.current.LMSSetValue("cmi.core.score.raw", "85");
          apiRef.current.LMSCommit("");
          setLessonStatus('completed');
          setScoreRaw('85');
          toast.success('Module terminé !');
        } else {
          apiRef.current.LMSSetValue("cmi.core.lesson_status", "incomplete");
          apiRef.current.LMSCommit("");
        }
      }
    };

    return (
      <div className="w-full h-full bg-white flex flex-col" data-testid="scorm-demo-content">
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-8 py-4 flex items-center justify-between">
          <h2 className="text-white font-semibold">{slides[demoSlide].title}</h2>
          <span className="text-teal-100 text-sm">{demoSlide + 1} / {slides.length}</span>
        </div>
        <div className="flex-1 flex items-center justify-center p-12">
          <div className="max-w-2xl text-center">
            <p className="text-lg text-slate-700 leading-relaxed">{slides[demoSlide].content}</p>
          </div>
        </div>
        <div className="border-t border-slate-200 px-8 py-4 flex items-center justify-between bg-slate-50">
          <div className="flex gap-1">
            {slides.map((_, i) => (
              <button key={i} onClick={() => goToSlide(i)}
                className={`w-8 h-2 rounded-full transition-all ${i === demoSlide ? 'bg-teal-500' : i < demoSlide ? 'bg-teal-200' : 'bg-slate-200'}`} />
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={demoSlide === 0}
              onClick={() => goToSlide(demoSlide - 1)} data-testid="demo-prev">
              Précédent
            </Button>
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700"
              disabled={demoSlide === slides.length - 1}
              onClick={() => goToSlide(demoSlide + 1)} data-testid="demo-next">
              Suivant
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-bold mb-2">Module introuvable</h2>
          <Button variant="outline" className="text-white border-white/20" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />Retour
          </Button>
        </div>
      </div>
    );
  }

  const statusColor = {
    'completed': 'text-emerald-400 border-emerald-800',
    'passed': 'text-emerald-400 border-emerald-800',
    'incomplete': 'text-blue-400 border-blue-800',
    'not attempted': 'text-slate-400 border-slate-700',
    'failed': 'text-red-400 border-red-800',
    'browsed': 'text-amber-400 border-amber-800',
  };

  const statusLabel = {
    'completed': 'Terminé', 'passed': 'Réussi', 'incomplete': 'En cours',
    'not attempted': 'Non commencé', 'failed': 'Échoué', 'browsed': 'Consulté',
  };

  const hasRealContent = module.file_id && module.file_id.length > 5;

  return (
    <div ref={containerRef} className="min-h-screen bg-slate-950 flex flex-col" data-testid="scorm-player">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 bg-slate-900/80 border-b border-slate-800 backdrop-blur-sm shrink-0">
        <div className="flex items-center gap-4">
          <Button variant="ghost" className="text-slate-400 hover:text-white h-8 px-2"
            onClick={handleExit} data-testid="scorm-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />Retour
          </Button>
          <div>
            <h1 className="text-sm font-semibold text-white">{module.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-700">
                SCORM {module.version}
              </Badge>
              <Badge variant="outline" className={`text-[10px] ${statusColor[lessonStatus] || statusColor['not attempted']}`}>
                {statusLabel[lessonStatus] || lessonStatus}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {started && (
            <>
              <span className="text-xs text-slate-500">
                <Clock className="h-3 w-3 inline mr-1" />{formatTime(sessionTime)}
              </span>
              {scoreRaw !== null && (
                <span className="text-xs text-emerald-400 font-bold" data-testid="scorm-score">Score: {scoreRaw}%</span>
              )}
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white h-7 px-2"
                onClick={handleManualSave} data-testid="scorm-save-btn">
                <Save className="h-3.5 w-3.5 mr-1" />Sauvegarder
              </Button>
              {lastSaved && (
                <span className="text-[10px] text-slate-600">
                  Sauvé {lastSaved.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </>
          )}
          <button onClick={toggleFullscreen} className="text-slate-400 hover:text-white" data-testid="scorm-fullscreen">
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 flex items-center justify-center relative">
        {!started ? (
          <div className="text-center" data-testid="scorm-start-screen">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-teal-500/30">
              <Monitor className="h-16 w-16 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">{module.name}</h2>
            {module.description && (
              <p className="text-slate-400 mb-4 max-w-md mx-auto">{module.description}</p>
            )}
            <div className="flex items-center justify-center gap-4 mb-8 text-sm text-slate-500">
              <span>Version SCORM: {module.version}</span>
              {module.size && <span>Taille: {module.size}</span>}
              <span>Statut: {statusLabel[lessonStatus]}</span>
            </div>
            <div className="flex gap-3 justify-center">
              {lessonStatus !== 'not attempted' ? (
                <>
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 text-base"
                    onClick={handleResume} data-testid="scorm-resume-btn">
                    <RotateCcw className="h-5 w-5 mr-2" />Reprendre
                  </Button>
                  <Button variant="outline" className="text-white border-white/20 px-6 py-3"
                    onClick={() => { setLessonStatus('not attempted'); handleStart(); }} data-testid="scorm-restart-btn">
                    Recommencer
                  </Button>
                </>
              ) : (
                <Button className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 text-base"
                  onClick={handleStart} data-testid="scorm-start-btn">
                  <Play className="h-5 w-5 mr-2" />Commencer le module
                </Button>
              )}
            </div>
            {lessonStatus === 'completed' || lessonStatus === 'passed' ? (
              <div className="mt-6 flex items-center justify-center gap-2 text-emerald-400">
                <CheckCircle className="h-5 w-5" />
                <span className="font-semibold">Module déjà terminé{scoreRaw ? ` — Score: ${scoreRaw}%` : ''}</span>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="w-full h-full" data-testid="scorm-content-area">
            {hasRealContent ? (
              /* Real SCORM package loaded in iframe */
              <iframe
                ref={iframeRef}
                src={`${API}/upload/${module.file_id}/download`}
                className="w-full h-full border-0"
                title={module.name}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                data-testid="scorm-iframe"
              />
            ) : (
              /* Demo content when no real package */
              <DemoScormContent />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScormPlayer;
